import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2,
  Link as LinkIcon, Save, History, NotebookText,
} from 'lucide-react';
import { narrativeExtensions, normaliseLink } from './narrativeExtensions';
import {
  FORM_NOTE_INSERTION,
  FormNoteInsertionContext,
  type FormNoteInsertionActions,
} from './formNoteInsertion';
import { FormNotesDrawer } from './FormNotesDrawer';
import {
  getReportSectionVersionContent,
  useReportSectionVersions,
  useSaveReportSection,
  type ReportSection,
} from '../../../lib/api/queries';
import {
  hasFormNotes,
  noteLabel,
  useFormNoteSources,
  useInsertFormNote,
  useKeepInsertion,
  useRemoveInsertion,
  type NoteSource,
} from '../../../lib/api/formNotes';

/**
 * Tiptap-backed editor for one narrative report section.
 *
 * Save creates a new revision. The version dropdown lists revisions
 * most-recent-first; picking an older revision loads its content into
 * the editor (but doesn't auto-save — the auditor still has to click
 * Save, which appends a new revision identical to the old content
 * rather than mutating history).
 *
 * Slice 19c — for per-template-section narratives, a Form notes drawer
 * sits beside the editor. Inserting a note places a marked block and saves;
 * Keep and Remove on the block act on it and save. The server reconciles
 * markers against its ledger on every save, so a half-finished flow (one
 * call failed) converges rather than leaving the two out of step.
 */
export function NarrativeSectionEditor({
  tenantId,
  auditId,
  section,
  editable,
  notesOpen,
  onToggleNotes,
  attentionCount,
}: {
  tenantId: string;
  auditId: string;
  section: ReportSection;
  editable: boolean;
  /** Form notes drawer open (remembered per browser by the Report tab). */
  notesOpen: boolean;
  onToggleNotes: () => void;
  /** Items in this section awaiting review or changed since kept. */
  attentionCount: number;
}) {
  const save = useSaveReportSection(tenantId, auditId);
  const versions = useReportSectionVersions(tenantId, auditId, section.key);
  const withNotes = hasFormNotes(section.key);
  const noteSources = useFormNoteSources(tenantId, auditId, section.key, withNotes);
  const insertNote = useInsertFormNote(tenantId, auditId);
  const keepInsertion = useKeepInsertion(tenantId, auditId);
  const removeInsertion = useRemoveInsertion(tenantId, auditId);

  // Track whether the editor has unsaved changes since the last load /
  // server-confirmed save. Drives the Save button's disabled state.
  const [dirty, setDirty] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Has the author put the cursor in this section? If so a note goes in at
  // the cursor; if not, at the end (inserting at a default position 0
  // would drop it above the section's opening paragraph).
  const cursorPlaced = useRef(false);

  const editor = useEditor({
    // Slice 19b: the shared configuration only produces content the server
    // accepts (ADR-0002).
    extensions: narrativeExtensions(),
    content: parseContent(section.contentJson),
    editable,
    onUpdate: () => setDirty(true),
    onFocus: () => { cursorPlaced.current = true; },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[20rem] p-4',
      },
    },
  }, [section.id]);

  // When the section in props changes (user switched section), reset the
  // editor content to the new section's saved content. Otherwise stale
  // content from the previous section bleeds across.
  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(parseContent(section.contentJson), false);
    setDirty(false);
  }, [editor, section.id, section.contentJson]);

  useEffect(() => { cursorPlaced.current = false; }, [section.id]);

  /** Saves the editor's current content. Resolves false when the save failed. */
  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!editor) return false;
    setServerError(null);
    try {
      const json = JSON.stringify(editor.getJSON());
      await save.mutateAsync({ sectionKey: section.key, contentJson: json });
      setDirty(false);
      return true;
    } catch (err) {
      setServerError(errorMessage(err));
      return false;
    }
  }, [editor, save, section.key]);

  const onSave = () => { void saveNow(); };

  const onPickVersion = async (revision: number) => {
    if (!editor) return;
    const v = await getReportSectionVersionContent(auditId, section.key, revision);
    editor.commands.setContent(parseContent(v.contentJson), false);
    setDirty(true); // loaded an old revision — saving will append as new
  };

  // ---- Form notes (Slice 19c) -------------------------------------------

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    noteSources.data?.items.forEach(item => {
      if (item.latestInsertion) map.set(item.latestInsertion.insertionId, noteLabel(item));
    });
    return map;
  }, [noteSources.data]);

  const notesBusy = save.isPending || insertNote.isPending
    || keepInsertion.isPending || removeInsertion.isPending;

  /** Adds the note as a marked block (at the cursor, else at the end) and saves. */
  const addNote = useCallback(async (source: NoteSource) => {
    if (!editor) return;
    if (source.status === 'Changed'
        && !window.confirm('The earlier version of this note is already in the report. ' +
                           'Add the updated note as well?')) {
      return;
    }
    setServerError(null);
    try {
      const inserted = await insertNote.mutateAsync({
        sectionKey: section.key, criterionItemId: source.criterionItemId,
      });
      const block = (JSON.parse(inserted.contentJson) as { content: object[] }).content[0];
      const { state } = editor;
      const { $from } = state.selection;
      const at = cursorPlaced.current && $from.depth >= 1 ? $from.after(1) : state.doc.content.size;
      editor.chain().insertContentAt(at, block).run();
      if (await saveNow()) scrollToInsertion(inserted.insertionId);
    } catch (err) {
      setServerError(errorMessage(err));
    }
  }, [editor, insertNote, saveNow, section.key]);

  /**
   * Keep: accept on the server first, then drop the marker and save. The
   * other order would let the save's reconciler see a pending block gone
   * missing and record it as removed.
   */
  const keep = useCallback(async (insertionId: string) => {
    if (!editor) return;
    setServerError(null);
    try {
      await keepInsertion.mutateAsync(insertionId);
    } catch (err) {
      setServerError(errorMessage(err));
      return;
    }
    const found = findInsertion(editor, insertionId);
    if (found) {
      editor.view.dispatch(editor.state.tr.replaceWith(
        found.pos, found.pos + found.node.nodeSize, found.node.content));
    }
    await saveNow();
  }, [editor, keepInsertion, saveNow]);

  /**
   * Remove: delete the block and save (the reconciler marks it removed),
   * then tell the server directly so it's recorded who removed it.
   */
  const remove = useCallback(async (insertionId: string) => {
    if (!editor) return;
    const found = findInsertion(editor, insertionId);
    if (found) {
      editor.view.dispatch(editor.state.tr.delete(found.pos, found.pos + found.node.nodeSize));
      if (!await saveNow()) return;
    }
    try {
      await removeInsertion.mutateAsync(insertionId);
    } catch (err) {
      setServerError(errorMessage(err));
    }
  }, [editor, removeInsertion, saveNow]);

  /** A pending block whose note has changed: swap in the current text. */
  const replace = useCallback(async (source: NoteSource) => {
    const pending = source.latestInsertion;
    if (!pending) return;
    await remove(pending.insertionId);
    await addNote({ ...source, status: 'NotUsed' });
  }, [remove, addNote]);

  const insertionActions = useMemo<FormNoteInsertionActions>(() => ({
    labelFor: id => labelById.get(id),
    keep: id => { void keep(id); },
    remove: id => { void remove(id); },
    busy: notesBusy,
  }), [labelById, keep, remove, notesBusy]);

  const showDrawer = withNotes && notesOpen;

  return (
    <FormNoteInsertionContext.Provider value={insertionActions}>
      <div className={showDrawer ? 'flex items-start gap-4' : ''}>
        <div className="space-y-3 flex-1 min-w-0">
          <header className="bg-accent-alt-50 border border-accent-alt-100 rounded-lg px-5 py-3">
            <p className="text-xs font-semibold text-accent-alt-700 uppercase tracking-wide">
              {section.currentRevision ? `Revision ${section.currentRevision}` : 'New section'}
            </p>
            <h2 className="text-lg font-semibold text-gray-900 mt-0.5">{section.title}</h2>
          </header>

          {editor && (
            <>
              <Toolbar editor={editor} disabled={!editable}
                       notes={withNotes
                         ? { open: notesOpen, count: attentionCount, onToggle: onToggleNotes }
                         : null} />
              {/* The text scrolls on its own, so the title, toolbar and
                  Save stay in view on a long section. */}
              <div className="bg-white border border-gray-200 rounded-md overflow-y-auto
                              min-h-[20rem] max-h-[calc(100vh-20rem)]">
                <EditorContent editor={editor} />
              </div>
            </>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <VersionPicker
              versions={versions.data ?? []}
              loading={versions.isLoading}
              currentRevision={section.currentRevision}
              onPick={onPickVersion}
            />

            <div className="flex items-center gap-3">
              {serverError && <p className="text-sm text-danger-500">{serverError}</p>}
              {dirty && <span className="text-xs text-gray-500">Unsaved changes</span>}
              <button type="button"
                      disabled={!editable || !dirty || save.isPending}
                      onClick={onSave}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                                 text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                                 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Save className="w-4 h-4" aria-hidden />
                {save.isPending ? 'Saving…' : 'Save section'}
              </button>
            </div>
          </div>
        </div>

        {showDrawer && (
          <FormNotesDrawer
            tenantId={tenantId}
            auditId={auditId}
            sectionKey={section.key}
            editable={editable}
            busy={notesBusy}
            onInsert={source => { void addNote(source); }}
            onReplace={source => { void replace(source); }}
            onGoTo={scrollToInsertion}
            onClose={onToggleNotes}
          />
        )}
      </div>
    </FormNoteInsertionContext.Provider>
  );
}

/** The top-level insertion block with this id, and where it starts. */
function findInsertion(editor: Editor, insertionId: string) {
  let found: { pos: number; node: Editor['state']['doc'] } | null = null;
  editor.state.doc.forEach((node, offset) => {
    if (!found && node.type.name === FORM_NOTE_INSERTION && node.attrs.insertionId === insertionId) {
      found = { pos: offset, node };
    }
  });
  return found as { pos: number; node: Editor['state']['doc'] } | null;
}

function scrollToInsertion(insertionId: string) {
  // After a save the editor re-renders; wait a frame for the block to exist.
  window.requestAnimationFrame(() => {
    document.querySelector(`[data-insertion-id="${CSS.escape(insertionId)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

/**
 * A 400 from the save carries the reason as a plain string, e.g.
 * "Narrative content was rejected. $.content[0]: unsupported node type
 * 'table'" (Slice 19b); 409s from the notes endpoints do too.
 */
function errorMessage(err: unknown): string {
  const { status, body, detail } = err as { status?: number; body?: unknown; detail?: string };
  const reason = typeof body === 'string' ? body : detail;
  return reason ?? (status ? `Request failed (${status})` : 'Request failed');
}

function Toolbar({
  editor, disabled, notes,
}: {
  editor: Editor;
  disabled: boolean;
  /** Form notes button (Slice 19c); null for sections without form notes. */
  notes: { open: boolean; count: number; onToggle: () => void } | null;
}) {
  const btn = (active: boolean) =>
    'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ' +
    (disabled
      ? 'text-gray-300 cursor-not-allowed'
      : active
        ? 'bg-accent-alt-100 text-accent-alt-700'
        : 'text-gray-600 hover:bg-gray-100');

  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1 border border-gray-200 rounded-md bg-gray-50">
      <button type="button" title="Heading"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={btn(editor.isActive('heading', { level: 2 }))}>
        <Heading2 className="w-4 h-4" aria-hidden />
      </button>
      <button type="button" title="Bold"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={btn(editor.isActive('bold'))}>
        <Bold className="w-4 h-4" aria-hidden />
      </button>
      <button type="button" title="Italic"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={btn(editor.isActive('italic'))}>
        <Italic className="w-4 h-4" aria-hidden />
      </button>
      <button type="button" title="Underline"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={btn(editor.isActive('underline'))}>
        <UnderlineIcon className="w-4 h-4" aria-hidden />
      </button>
      <span className="w-px h-5 bg-gray-200 mx-1" />
      <button type="button" title="Bulleted list"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={btn(editor.isActive('bulletList'))}>
        <List className="w-4 h-4" aria-hidden />
      </button>
      <button type="button" title="Numbered list"
              disabled={disabled}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={btn(editor.isActive('orderedList'))}>
        <ListOrdered className="w-4 h-4" aria-hidden />
      </button>
      <span className="w-px h-5 bg-gray-200 mx-1" />
      <button type="button" title="Link"
              disabled={disabled}
              onClick={() => {
                const input = window.prompt('Link address (web or email)');
                if (!input) return;
                // Only http(s) and mailto links are saved (ADR-0002), so
                // "example.com" becomes https://example.com here rather than
                // failing when the section is saved.
                const href = normaliseLink(input);
                if (!href) {
                  window.alert('That doesn\'t look like a web or email address.');
                  return;
                }
                editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
              }}
              className={btn(editor.isActive('link'))}>
        <LinkIcon className="w-4 h-4" aria-hidden />
      </button>
      {notes && (
        <button type="button"
                onClick={notes.onToggle}
                aria-pressed={notes.open}
                title={notes.open ? 'Hide form notes' : 'Show form notes'}
                className={'ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border ' +
                           'text-sm font-medium transition-colors ' +
                           (notes.open
                             ? 'border-accent-alt-500/40 bg-accent-alt-50 text-accent-alt-700'
                             : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-100')}>
          <NotebookText className="w-4 h-4" aria-hidden />
          Form notes
          {notes.count > 0 && (
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-amber-200 text-amber-900 text-xs
                             font-semibold inline-flex items-center justify-center"
                  title={`${notes.count} to review`}>
              {notes.count}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

function VersionPicker({
  versions, loading, currentRevision, onPick,
}: {
  versions: { revision: number; createdAtUtc: string; createdByUserId: string }[];
  loading: boolean;
  currentRevision: number | null;
  onPick: (revision: number) => void;
}) {
  if (loading) return <span className="text-xs text-gray-500">Loading versions…</span>;
  if (!versions.length) return <span className="text-xs text-gray-500">No previous revisions</span>;

  return (
    <label className="inline-flex items-center gap-2 text-xs text-gray-600">
      <History className="w-4 h-4 text-gray-400" aria-hidden />
      <span>Load version</span>
      <select className="px-2 py-1 border border-gray-300 rounded text-xs"
              defaultValue=""
              onChange={e => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) onPick(v);
                e.target.value = ''; // reset so the same revision can be picked again
              }}>
        <option value="">Choose…</option>
        {versions.map(v => (
          <option key={v.revision} value={v.revision}>
            Rev {v.revision} — {new Date(v.createdAtUtc).toLocaleString()}
            {v.revision === currentRevision ? ' (current)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Tiptap accepts either a JSON object or HTML. We persist the JSON
 * stringified, so we parse it back into an object before handing it to
 * setContent. A null / empty string maps to a fresh empty doc.
 */
function parseContent(raw: string | null): object | string {
  if (!raw) return '';
  try {
    return JSON.parse(raw);
  } catch {
    // If older content was saved as plain HTML, fall back to passing the
    // string — Tiptap parses HTML inputs too.
    return raw;
  }
}
